/** @format */

import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
	ArrowLeft,
	ArrowRight,
	RotateCcw,
	Sun,
	Moon,
	Eye,
	EyeOff,
	Layers,
	Home,
	Bed,
	Thermometer,
	Sparkles,
	Zap,
	HelpCircle,
	Smartphone,
	User,
} from "lucide-react"; // Import relevant icons
import Stepper from "./Stepper";
import ContactBlock from "./contact/ContactBlock";

// --- Stepper Component (Assuming it exists and accepts steps, currentStep) ---

// --- Quiz Data Structure ---
const quizQuestions = [
	{
		id: "q1",
		question: "What's your main goal for this window?",
		options: [
			{
				value: "max_light_block",
				text: "Block Maximum Light",
				description: "For bedrooms, media rooms (total darkness).",
				icon: Moon,
			},
			{
				value: "balance_light_privacy",
				text: "Balance Light & Privacy",
				description: "Filter light gently, maintain privacy.",
				icon: Sun,
			}, // Could use Half Sun/Moon icon if available
			{
				value: "preserve_view",
				text: "Preserve the View",
				description: "See outside while reducing glare/heat.",
				icon: Eye,
			},
			{
				value: "insulation",
				text: "Improve Insulation",
				description: "Better temperature control, energy savings.",
				icon: Thermometer,
			}, // Or Snowflake/Sun
		],
	},
	{
		id: "q2",
		question: "Which room is this shade for?",
		options: [
			{ value: "bedroom", text: "Bedroom / Nursery", icon: Bed },
			{ value: "living", text: "Living / Dining Room", icon: Home }, // Could use TV or Sofa icon
			{ value: "office", text: "Office / Study", icon: Layers }, // Or Briefcase icon
			{
				value: "kitchen_bath",
				text: "Kitchen / Bathroom",
				description: "(Consider moisture resistance)",
				icon: Home,
			}, // Could use Droplet or Fork/Knife icon
			{ value: "other", text: "Other Room", icon: Home },
		],
	},
	{
		id: "q3",
		question:
			"How important is daytime privacy (preventing viewing IN from outside)?",
		// Simplified based on initial goal, could be more nuanced
		condition: (answers) => answers.q1 !== "max_light_block", // Only ask if not already choosing max blackout
		options: [
			{
				value: "high_privacy",
				text: "Very Important",
				description: "No visibility inwards during the day.",
				icon: EyeOff,
			},
			{
				value: "moderate_privacy",
				text: "Somewhat Important",
				description: "Silhouettes might be slightly visible.",
				icon: EyeOff,
			}, // Use slightly different icon maybe?
			{
				value: "low_privacy",
				text: "Not Important",
				description: "Maximizing light/view is key.",
				icon: Eye,
			},
		],
	},
	{
		id: "q4",
		question: "Which style best describes your preference?",
		options: [
			{
				value: "modern_sleek",
				text: "Modern & Sleek",
				description: "Clean lines, minimalist look.",
				icon: Layers,
			},
			{
				value: "soft_textured",
				text: "Soft & Textured",
				description: "Adds warmth, visual interest.",
				icon: Sparkles,
			}, // Or a Fabric icon
			{
				value: "unique_statement",
				text: "Unique & Statement",
				description: "Distinctive patterns or operation.",
				icon: Zap,
			}, // Or Star icon
		],
	},
];

// --- Recommendation Logic ---
// This is a simplified example, needs refinement based on your product specifics
const getRecommendations = (answers) => {
	const recs = [];
	const reasons = {}; // Store reasons for recommendations

	// Helper to add recommendation with reason
	const addRec = (type, reasonKey, reasonText, priority = 3) => {
		const existing = recs.find((r) => r.type === type);
		if (!existing) {
			recs.push({ type, priority }); // Lower number = higher priority
			reasons[type] = new Set();
		}
		// Add reason only if the type already exists (to avoid duplicate reasons array)
		if (reasons[type] && reasonKey) {
			reasons[type].add(reasonText);
		} else if (reasonKey) {
			reasons[type] = new Set([reasonText]); // Initialize if adding first reason
		}
	};

	// Q1: Main Goal
	if (answers.q1 === "max_light_block") {
		addRec("Blackout Roller", "q1", "Provides complete darkness", 1);
		addRec("Blackout Cellular", "q1", "Provides complete darkness", 1); // Cellular often has better edge sealing
	}
	if (answers.q1 === "balance_light_privacy") {
		addRec("Privacy Roller", "q1", "Balances light and privacy", 1);
		addRec(
			"Light Filtering Cellular",
			"q1",
			"Softly filters light with privacy",
			1
		);
		addRec("Zebra", "q1", "Offers versatile light/privacy control", 1);
		addRec("Light Filtering Sheer", "q1", "Softens light with some privacy", 2);
	}
	if (answers.q1 === "preserve_view") {
		addRec("Solar Roller", "q1", "Reduces glare/heat while keeping view", 1);
		addRec("Sheer", "q1", "Maximizes view and natural light", 1);
		addRec("Zebra", "q1", "Can align for view-through", 2);
	}
	if (answers.q1 === "insulation") {
		addRec("Cellular", "q1", "Excellent insulation properties", 1); // All cellular types
		addRec("Blackout Cellular", "q1", "Best insulation + darkness", 1);
		addRec("Light Filtering Cellular", "q1", "Good insulation + soft light", 1);
	}

	// Q2: Room Type (Refine based on Q1)
	if (answers.q2 === "bedroom") {
		// Boost priority of Blackout options
		recs.forEach((r) => {
			if (r.type.includes("Blackout")) r.priority = 0;
		});
		if (!recs.some((r) => r.type.includes("Blackout"))) {
			// Add if not already there
			addRec("Blackout Roller", "q2", "Ideal for bedrooms");
			addRec("Blackout Cellular", "q2", "Ideal for bedrooms");
		}
	}
	if (answers.q2 === "kitchen_bath") {
		// Note: Add moisture resistance reason if applicable to specific types
		addRec("Solar Roller", "q2", "Durable for kitchens/baths", 2); // Often PVC based
		addRec(
			"Faux Wood Blinds",
			"q2",
			"Moisture resistant (If you offered them)",
			2
		); // Example if you had blinds
	}
	// Add other room logic...

	// Q3: Daytime Privacy (Refine based on Q1)
	if (answers.q3 === "high_privacy" && answers.q1 !== "max_light_block") {
		recs.forEach((r) => {
			if (r.type.includes("Privacy") || r.type.includes("Blackout"))
				r.priority -= 1;
		}); // Boost privacy/blackout
		if (
			!recs.some(
				(r) => r.type.includes("Privacy") || r.type.includes("Blackout")
			)
		) {
			addRec("Privacy Roller", "q3", "Ensures daytime privacy");
		}
	}
	if (answers.q3 === "low_privacy" && answers.q1 !== "max_light_block") {
		recs.forEach((r) => {
			if (r.type.includes("Sheer") || r.type.includes("Solar")) r.priority -= 1;
		}); // Boost sheer/solar
		if (
			!recs.some((r) => r.type.includes("Sheer") || r.type.includes("Solar"))
		) {
			addRec("Solar Roller", "q3", "Prioritizes view over privacy");
			addRec("Sheer", "q3", "Maximizes view");
		}
	}

	// Q4: Style
	if (answers.q4 === "modern_sleek") {
		recs.forEach((r) => {
			if (r.type.includes("Roller") || r.type.includes("Zebra"))
				r.priority -= 1;
		});
		addRec("Roller", "q4", "Offers a clean, modern look", 2);
		addRec("Zebra", "q4", "Provides a sleek, contemporary style", 2);
	}
	if (answers.q4 === "soft_textured") {
		recs.forEach((r) => {
			if (r.type.includes("Cellular") || r.type.includes("Sheer"))
				r.priority -= 1;
		});
		addRec("Cellular", "q4", "Soft fabric adds warmth", 2);
		addRec("Sheer", "q4", "Elegant and airy feel", 2);
		addRec("Textured Roller", "q4", "Many rollers offer texture", 3); // Lower priority mention
	}
	if (answers.q4 === "unique_statement") {
		addRec("Zebra", "q4", "Unique striped appearance", 1);
		addRec("Patterned Roller", "q4", "Rollers offer statement patterns", 2);
	}

	// Sort by priority (lower number first) and take top 3
	const sortedRecs = recs.sort((a, b) => a.priority - b.priority).slice(0, 3);

	// Map to final structure with details (add images, links etc.)
	const finalRecommendations = sortedRecs.map((rec) => {
		// Find matching product details (replace with your actual product data lookup)
		const details = productDetailsMap[rec.type] || {
			name: rec.type,
			link: `/shop`,
			image: "/images/placeholder.jpg",
			shortDesc: "A great choice.",
		};
		return {
			...details,
			reasons: Array.from(reasons[rec.type] || []), // Convert Set to Array
		};
	});

	return finalRecommendations;
};

// --- Placeholder Product Details Map ---
// Replace with actual data or fetch dynamically
// --- Product Details Map (Completed) ---
// Maps recommendation keys to display details & links
const productDetailsMap = {
	// --- Roller Shades ---
	"Blackout Roller": {
		name: "Blackout Roller Shades",
		link: "/shop?type=Roller&lightControl=Blackout", // Filter by type & light control
		image: "/images/newHome/shades/blackout_roller.webp",
		shortDesc: "100% light blockage for complete darkness and privacy.",
	},
	"Privacy Roller": {
		name: "Privacy Roller Shades",
		link: "/shop?type=Roller&lightControl=Privacy", // Filter by type & light control
		image: "/images/newHome/shades/privacy_roller2.webp",
		shortDesc: "Significantly darkens the room while ensuring full privacy.",
	},
	"Solar Roller": {
		name: "Solar Roller Shades",
		link: "/shop?type=Roller&lightControl=Solar", // Filter by type & light control
		image: "/images/newHome/shades/privacy_roller.jpg", // You provided this, double-check if it shows solar effect
		shortDesc: "Reduces heat & glare while preserving your outside view.",
	},
	"Textured Roller": {
		// Added based on Q4 logic
		name: "Textured Roller Shades",
		link: "/shop?type=Roller", // Link to general Roller, user needs to find textures
		image: "/images/newHome/shades/privacy_roller2.webp", // Placeholder - NEEDS texture image
		shortDesc:
			"Adds depth and visual interest with unique woven or natural textures.",
	},
	"Patterned Roller": {
		// Added based on Q4 logic
		name: "Patterned Roller Shades",
		link: "/shop?type=Roller", // Link to general Roller, user needs to find patterns
		image: "/images/newHome/shades/blackout_roller.webp", // Placeholder - NEEDS pattern image
		shortDesc:
			"Make a statement with stylish patterns, from subtle to bold designs.",
	},
	Roller: {
		// Generic Roller (if recommended for style)
		name: "Roller Shades",
		link: "/shop?type=Roller", // General Roller link
		image: "/images/newHome/shades/privacy_roller2.webp", // Use a common roller image
		shortDesc: "Sleek, modern, and available in many fabrics and styles.",
	},

	// --- Cellular Shades ---
	Cellular: {
		// Generic Cellular (if recommended for insulation/style)
		name: "Cellular Shades",
		link: "/shop?type=Simply+Cellular", // Main Cellular link (Note: space encoded as +)
		image: "/images/newHome/shades/day_night_cellular.webp",
		shortDesc: "Offer excellent insulation, light control, and a soft look.",
	},
	"Blackout Cellular": {
		name: "Blackout Cellular Shades",
		link: "/shop?type=Simply+Cellular&lightControl=Blackout", // Filtered link
		image: "/images/newHome/shades/blackout_cellular.webp",
		shortDesc: "Maximum insulation combined with complete light blockage.",
	},
	"Light Filtering Cellular": {
		name: "Light Filtering Cellular Shades",
		link: "/shop?type=Simply+Cellular&lightControl=Soft+Filter", // Filtered link (Note: space encoded as +)
		image: "/images/newHome/shades/light_cellular.webp",
		shortDesc: "Provide insulation while gently diffusing natural light.",
	},

	// --- Zebra Shades ---
	Zebra: {
		name: "Zebra Shades",
		link: "/shop?type=Zebra",
		image: "/images/newHome/shades/privacy_zebra.jpg",
		shortDesc:
			"Modern striped style with versatile, adjustable light and privacy control.",
	},

	// --- Sheer Shades ---
	Sheer: {
		// Generic Sheer (includes Transparent)
		name: "Sheer Shades",
		link: "/shop?type=Sheer",
		image: "/images/shadeinfo/shades/sheer/sheer.jpg",
		shortDesc: "Elegant fabrics that maximize natural light and outdoor views.",
	},
	"Light Filtering Sheer": {
		// If specifically recommended for light filtering balance
		name: "Light Filtering Sheer Shades",
		// Link might be the same as generic Sheer, or add a lightControl filter if applicable
		link: "/shop?type=Sheer&lightControl=Soft+Filter", // Assuming 'Soft Filter' applies
		image: "/images/shadeinfo/shades/sheer/sheer.jpg", // Reuse sheer image
		shortDesc:
			"Softens incoming light while offering a degree of daytime privacy.",
	},

	// --- Fallback (If a recommendation key isn't found) ---
	Default: {
		name: "Custom Shades",
		link: "/shop",
		image: "/images/logoDark-1.png", // Generic placeholder
		shortDesc: "Explore our full collection of custom window treatments.",
	},
};

// --- Main Quiz Screen Component ---
// `embedded` suppresses the page-level SEO tags below. The quiz is both its
// own route (/quiz) and a section inside the home page, and react-helmet-async
// lets the most recently mounted Helmet win — so when the home page rendered
// the quiz, the quiz's title, description and canonical replaced the home
// page's. The home page has been telling Google it is a duplicate of /quiz
// since March 2025.
const ProductQuiz = ({ embedded = false }) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [answers, setAnswers] = useState({});
	const [recommendations, setRecommendations] = useState([]);
	const [quizCompleted, setQuizCompleted] = useState(false);

	// --- Filter questions based on conditions ---
	const activeQuestions = useMemo(() => {
		return quizQuestions.filter((q) => !q.condition || q.condition(answers));
	}, [answers]);

	const totalSteps = activeQuestions.length;

	const currentQuestionData = activeQuestions[currentStep - 1];

	// --- Handlers ---
	const handleAnswerSelect = useCallback(
		(questionId, value) => {
			setAnswers((prev) => ({ ...prev, [questionId]: value }));
			// Optional: Auto-advance to next step
			// if (currentStep < totalSteps) {
			//     setCurrentStep(prev => prev + 1);
			// } else {
			//     // Calculate results on final question selection
			//     const results = getRecommendations({ ...answers, [questionId]: value });
			//     setRecommendations(results);
			//     setQuizCompleted(true);
			// }
		},
		[
			/* currentStep, totalSteps, answers */
		]
	); // Dependencies removed if not auto-advancing

	const handleNext = useCallback(() => {
		if (currentStep < totalSteps) {
			setCurrentStep((prev) => prev + 1);
		} else {
			// Calculate Recommendations
			console.log("Calculating Recommendations with answers:", answers);
			const results = getRecommendations(answers);
			console.log("Recommendations:", results);
			setRecommendations(results);
			setQuizCompleted(true);
			// window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll up to show results
		}
	}, [currentStep, totalSteps, answers]);

	const handleBack = useCallback(() => {
		if (currentStep > 1) {
			setCurrentStep((prev) => prev - 1);
		}
	}, [currentStep]);

	const handleRestart = useCallback(() => {
		setAnswers({});
		setCurrentStep(1);
		setRecommendations([]);
		setQuizCompleted(false);
	}, []);

	// Disable Next button if no answer selected for current step
	const isNextDisabled = !answers[currentQuestionData?.id];

	// Question/result headings sit under the home page's own h2 when embedded
	const Heading = embedded ? "h3" : "h2";
	const textLinkClass =
		"group/link inline-flex items-center gap-2 border-b border-[#141414] pb-1 uppercase tracking-[0.2em] text-xs font-medium text-[#141414]";

	return (
		<div className="font-inter text-[#141414]">
			{!embedded && (
				<Helmet>
					<title>
						Find Your Perfect Shade | Product Recommendation Quiz | Off Cut
						Shades
					</title>
					<meta
						name="description"
						content="Answer a few simple questions and let Off Cut Shades recommend the best custom window blinds or shades for your needs based on light control, privacy, style, and more."
					/>
					<link rel="canonical" href="https://offcutshades.com/quiz" />
					<meta name="robots" content="index, follow" />
				</Helmet>
			)}

			<main
				className={`max-w-[1320px] mx-auto px-4 sm:px-8 lg:px-14 ${
					embedded ? "" : "py-10 md:py-16"
				}`}
			>
				<div className="bg-[#F1EFEA] p-6 sm:p-10 lg:p-14">
					<AnimatePresence mode="wait">
						{!quizCompleted ? (
							// --- Quiz Question ---
							<motion.div
								key={currentStep}
								initial={{ opacity: 0, x: 24 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -24 }}
								transition={{ duration: 0.25, ease: "easeInOut" }}
							>
								{/* Progress */}
								<div
									className="grid gap-1.5"
									style={{
										gridTemplateColumns: `repeat(${totalSteps}, minmax(0, 1fr))`,
									}}
									aria-hidden="true"
								>
									{activeQuestions.map((q, i) => (
										<div
											key={q.id}
											className={`h-[2px] ${
												i < currentStep ? "bg-[#141414]" : "bg-[#D6D3CC]"
											}`}
										/>
									))}
								</div>
								<p className="mt-4 text-[11px] uppercase tracking-[0.18em] font-medium text-[#5E5E5A]">
									Question {currentStep} of {totalSteps}
								</p>

								<Heading className="mt-3 font-instrument text-[32px] md:text-[44px] font-normal leading-[1.05]">
									{currentQuestionData?.question}
								</Heading>
								<p className="mt-2 text-sm text-[#5E5E5A]">
									Select the option that best fits your needs.
								</p>

								{/* Options Grid */}
								<div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
									{currentQuestionData?.options.map((option) => {
										const isSelected =
											answers[currentQuestionData.id] === option.value;
										const IconComp = option.icon || HelpCircle; // Fallback icon
										return (
											<button
												key={option.value}
												type="button"
												aria-pressed={isSelected}
												onClick={() =>
													handleAnswerSelect(
														currentQuestionData.id,
														option.value
													)
												}
												className={`p-5 border text-left flex items-start gap-4 transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#141414] focus-visible:ring-offset-2 ${
													isSelected
														? "border-[#141414] bg-[#141414] text-[#FAFAF7]"
														: "border-[#D6D3CC] bg-[#FAFAF7] text-[#141414] hover:border-[#141414]"
												}`}
											>
												<IconComp
													size={20}
													strokeWidth={1.5}
													className="mt-0.5 flex-shrink-0"
													aria-hidden="true"
												/>
												<span className="block">
													<span className="block text-base font-medium">
														{option.text}
													</span>
													{option.description && (
														<span
															className={`block mt-1 text-[13px] font-normal ${
																isSelected ? "text-[#FAFAF7]/70" : "text-[#5E5E5A]"
															}`}
														>
															{option.description}
														</span>
													)}
												</span>
											</button>
										);
									})}
								</div>

								{/* Navigation Buttons */}
								<div className="flex justify-between items-center mt-10">
									<button
										type="button"
										onClick={handleBack}
										disabled={currentStep === 1}
										className="inline-flex items-center gap-2 text-sm text-[#5E5E5A] hover:text-[#141414] disabled:opacity-0 disabled:pointer-events-none"
									>
										<ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
										Back
									</button>
									<button
										type="button"
										onClick={handleNext}
										disabled={isNextDisabled}
										className="group/next inline-flex items-center gap-2 min-h-[48px] px-7 bg-[#141414] text-[#FAFAF7] text-sm font-medium transition-colors duration-150 disabled:bg-[#141414]/25 disabled:cursor-not-allowed"
									>
										{currentStep === totalSteps
											? "Show my recommendations"
											: "Next question"}
										<ArrowRight
											size={16}
											strokeWidth={1.5}
											aria-hidden="true"
											className="transition-transform duration-300 group-hover/next:translate-x-1"
										/>
									</button>
								</div>
							</motion.div>
						) : (
							// --- Quiz Results ---
							<motion.div
								key="results"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.3, ease: "easeOut" }}
							>
								<p className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#5E5E5A]">
									Your results
								</p>
								<Heading className="mt-3 font-instrument text-[32px] md:text-[44px] font-normal leading-[1.05]">
									Your shade recommendations
								</Heading>
								<p className="mt-2 text-[#5E5E5A] max-w-lg">
									Based on your answers, here are the top shade types we think
									you'll love:
								</p>

								{/* Recommendations Grid */}
								<div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
									{recommendations.map((rec, index) => (
										<motion.article
											key={rec.name}
											initial={{ opacity: 0, y: 12 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.1 + index * 0.08 }}
											className="flex flex-col"
										>
											<div className="overflow-hidden bg-[#E4E2DC]">
												<img
													src={rec.image}
													alt={rec.name}
													className="w-full aspect-[4/3] object-cover"
												/>
											</div>
											<h4 className="mt-4 text-base font-medium">{rec.name}</h4>
											<p className="mt-1 text-sm text-[#5E5E5A]">
												{rec.shortDesc}
											</p>
											{rec.reasons && rec.reasons.length > 0 && (
												<div className="mt-4">
													<p className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#5E5E5A] mb-2">
														Why we recommend it
													</p>
													<ul className="list-disc list-outside pl-4 space-y-1 text-[13px] text-[#141414]/80">
														{rec.reasons.map((reason, rIndex) => (
															<li key={rIndex}>{reason}</li>
														))}
													</ul>
												</div>
											)}
											<div className="mt-auto pt-5">
												<Link
													to={rec.link}
													className={textLinkClass}
													aria-label={`View ${rec.name}`}
												>
													View details
													<ArrowRight
														size={16}
														strokeWidth={1.5}
														aria-hidden="true"
														className="transition-transform duration-300 group-hover/link:translate-x-1"
													/>
												</Link>
											</div>
										</motion.article>
									))}
								</div>

								{/* Further Actions */}
								<div className="mt-12 pt-8 border-t border-[#D6D3CC] flex flex-wrap items-center gap-x-8 gap-y-4">
									<Link to="/swatches" className={textLinkClass}>
										Order free samples
										<ArrowRight
											size={16}
											strokeWidth={1.5}
											aria-hidden="true"
											className="transition-transform duration-300 group-hover/link:translate-x-1"
										/>
									</Link>
									<button
										type="button"
										onClick={handleRestart}
										className="inline-flex items-center gap-2 text-sm text-[#5E5E5A] hover:text-[#141414]"
									>
										<RotateCcw size={14} strokeWidth={1.5} aria-hidden="true" />
										Retake quiz
									</button>
									<Link
										to="/shop"
										className="text-sm text-[#5E5E5A] underline underline-offset-4 decoration-1 hover:text-[#141414]"
									>
										Browse all shades
									</Link>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</main>
		</div>
	);
};

export default ProductQuiz;

